import React from 'react';
import i18n from 'i18n-js';
import { Stack, IconButton, List } from '@mui/material';
import { useStyles } from './ListWithActions.styled';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Edit as EditIcon,
} from '@material-ui/icons';
import { SearchField } from 'components/FormFields';
import PropTypes from 'prop-types';
import { InputAdornment } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paginator from 'components/Paginator';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const CustomListItem = ({ item, classes, onEdit, onDelete, onSelect }) => {
  const isEmail = item.type === 'email';

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {isEmail && <EmailIcon data-cy="email-icon" />}
        <p className={classes.item_label} data-cy="item-name">
          {item.name}
        </p>
      </Stack>

      <Stack direction="row" spacing={1}>
        {onEdit && (
          <IconButton data-cy="edit-button" onClick={() => onEdit(item)}>
            <EditIcon />
          </IconButton>
        )}
        {onDelete && (
          <IconButton data-cy="delete-button" onClick={() => onDelete(item)}>
            <DeleteIcon />
          </IconButton>
        )}
        {onSelect && (
          <IconButton data-cy="select-button" onClick={() => onSelect(item)}>
            <ArrowForwardIosIcon />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
};

const ListWithActions = ({
  dataCy,
  data,
  onEdit,
  onDelete,
  onSelect,
  searchType,
  handleSearch,
  params,
  loading,
  pagination,
  handlePageChange,
}) => {
  const classes = useStyles();

  return (
    <Stack>
      {searchType === 'search' && (
        <SearchField
          margin="none"
          label={`${i18n.t('destinations.search')}`}
          data-cy="search-destinations"
          field={{ onChange: handleSearch, name: 'keyword', value: params.keyword }}
          inputProps={{
            startAdornment: (
              <InputAdornment
                classes={{
                  root: classes.searchIcon,
                }}
                position="start"
              >
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      )}
      {loading && <LinearProgress style={{ marginTop: 1 }} />}
      <List data-cy={dataCy}>
        {data.map((item) => (
          <CustomListItem
            key={item.id}
            item={item}
            onEdit={onEdit}
            classes={classes}
            onDelete={onDelete}
            onSelect={onSelect}
          />
        ))}
      </List>
      {pagination && data.length > 0 && (
        <Paginator
          component="div"
          limit={params.limit}
          onPageChange={handlePageChange}
          pagination={pagination}
          rowsPerPageOptions={[5]}
        />
      )}
    </Stack>
  );
};

ListWithActions.propTypes = {
  dataCy: PropTypes.string,
  data: PropTypes.array,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSelect: PropTypes.func,
  searchType: PropTypes.string,
  handleSearch: PropTypes.func,
  params: PropTypes.object,
  loading: PropTypes.bool,
  pagination: PropTypes.object,
  handlePageChange: PropTypes.func,
};

CustomListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string, // assuming 'email' or other types
  }).isRequired,
  classes: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onSelect: PropTypes.func,
};

ListWithActions.defaultProps = {
  data: [],
  dataCy: 'listWithActions',
  searchType: '',
  loading: false,
  onEdit: null,
  onDelete: null,
  onSelect: null,
};

export default ListWithActions;
